/**
 * toastBanner — drop-in sonner replacement.
 *
 * Routes `toast.success(...)` and `toast.info(...)` to an in-page banner
 * (rendered by <BannerHost>, mounted once per layout). `toast.error(...)`
 * and `toast.warning(...)` still flow through to sonner as floating
 * toast notifications.
 *
 * Swap `import { toast } from "sonner";` → `import { toast } from "@/lib/toastBanner";`
 * anywhere you want success affirmations to appear inline instead of as
 * floating toasts — zero other code changes required.
 */
import { toast as sonnerToast } from "sonner";

const listeners = new Set();
let nextId = 1;

function emit(event) {
  for (const fn of listeners) {
    try { fn(event); } catch { /* noop */ }
  }
}

export function subscribeBanner(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function pushBanner(type, message, opts) {
  if (message === undefined || message === null) return;
  const id = nextId++;
  const ttl = opts && typeof opts.duration === "number" ? opts.duration : 4500;
  emit({ kind: "add", banner: { id, type, message: String(message), ttl } });
  return id;
}

export function dismissBanner(id) {
  emit({ kind: "dismiss", id });
}

/**
 * `toast` — forwards `.error`, `.warning`, `.loading`, `.promise`, etc. to
 * sonner. `.success` and `.info` are intercepted and become inline
 * banners. Shape matches the subset of sonner's API we actually use in
 * this codebase (verified: no raw `toast(message)` calls, only dotted).
 */
export const toast = {
  success: (message, opts) => pushBanner("success", message, opts),
  info: (message, opts) => pushBanner("info", message, opts),
  error: (message, opts) => sonnerToast.error(message, opts),
  warning: (message, opts) => sonnerToast.warning?.(message, opts) ?? sonnerToast.error(message, opts),
  loading: (message, opts) => sonnerToast.loading?.(message, opts),
  dismiss: (id) => sonnerToast.dismiss?.(id),
  promise: (p, msgs) => sonnerToast.promise?.(p, msgs),
};
